import {EGameMode, gameModeState} from '@/domains/Store/atoms'
import AudioManager from '@repo/shared/AudioManager'
import clsx from 'clsx'
import gsap from 'gsap'
import {useAtomValue} from 'jotai'
import {useCallback, useEffect, useRef, useState} from 'react'

const audioManager = new AudioManager()

const IN_ANIM_DURATION = 0.4
const IN_ANIM_DELAY = 0

interface CircleCountdownProps {
  countdownFromSeconds: number
  startTime: number
  serverTimeDiff: number
  onEnd: () => void
}

export const CircleCountdown = ({
  countdownFromSeconds,
  startTime,
  serverTimeDiff,
  onEnd,
}: CircleCountdownProps) => {
  const gameMode = useAtomValue(gameModeState)

  const circleRef = useRef<HTMLDivElement>(null)
  const isAnimRunning = useRef(false)
  const [countdown, setCountdown] = useState<number | null>(null)

  const usedSeconds = useRef(countdownFromSeconds)

  /**
   * Circle animation
   */
  const circleAnim = useCallback((countdownFromSeconds: number = 5, secondsDiff: number) => {
    // calculate the start percentage for correc representation
    const startPercentage = 100 - (secondsDiff / countdownFromSeconds) * 100

    // calculate the elapsed time since the countdown started
    const elapsedTime = countdownFromSeconds - secondsDiff

    // calculate the remaining time and use it as the duration
    const remainingTime = countdownFromSeconds - elapsedTime - IN_ANIM_DELAY

    // create a gsap timeline for the circle animation
    var tl = gsap.timeline({repeat: 0})
    tl.set(circleRef.current, {
      scale: 0,
      backgroundImage: `conic-gradient(#071144 ${startPercentage}%, #0711447a 0)`,
    })
    tl.to(circleRef.current, {
      scale: 1,
      duration: IN_ANIM_DURATION,
      ease: 'back',
      delay: IN_ANIM_DELAY,
    })
    tl.to(
      circleRef.current,
      {
        backgroundImage: 'conic-gradient(#071144 100%, #0711447a 0)',
        duration: remainingTime,
        ease: 'none',
      },
      '<',
    )
  }, [])

  // countdown logic
  const intervalTimer = useRef<NodeJS.Timeout | null>()

  const updateCountdown = useCallback(() => {
    // get the local time with the server time difference
    const localServerTime = new Date(Date.now() + serverTimeDiff)

    // get the start time from the server
    const startAtServerTime = new Date(startTime ?? 0)

    // get the difference between the start time and the local offset time
    const secondsDiff = (startAtServerTime.getTime() - localServerTime.getTime()) / 1000

    if (secondsDiff < 0.5) {
      // we're done, clean up and enter next stage
      clearInterval(intervalTimer.current!)
      setCountdown(0)
      isAnimRunning.current = false

      if (usedSeconds.current !== Math.round(secondsDiff)) {
        usedSeconds.current = Math.round(secondsDiff)

        if (usedSeconds.current < 1) {
          audioManager.SFX.countdown_tick_final?.stop()
          audioManager.SFX.countdown_tick_final?.seek(0)
          audioManager.SFX.countdown_tick_final?.play()
        }
      }

      gsap.to(circleRef.current, {
        scale: 0,
        duration: 0.3,
        ease: 'back.in',
        delay: 0,
        onComplete: () => {
          setCountdown(null)
          audioManager.SFX.starting_pistol?.seek(0)
          audioManager.SFX.starting_pistol?.fade(1, 0, 2000).play()

          onEnd()
        },
      })
    } else {
      // update the countdown
      setCountdown(secondsDiff)

      if (usedSeconds.current !== Math.round(secondsDiff)) {
        usedSeconds.current = Math.round(secondsDiff)

        if (usedSeconds.current >= 1) {
          audioManager.SFX.countdown_tick?.stop()
          audioManager.SFX.countdown_tick?.seek(0)
          audioManager.SFX.countdown_tick?.play()
        }
      }

      if (!isAnimRunning.current) {
        circleAnim(countdownFromSeconds, secondsDiff)
      }

      isAnimRunning.current = true
    }
  }, [countdownFromSeconds, serverTimeDiff, onEnd])

  /**
   * Start the countdown when the gameStarted message is received
   */
  useEffect(() => {
    intervalTimer.current = setInterval(updateCountdown, 50)

    return () => {
      if (intervalTimer.current) {
        clearInterval(intervalTimer.current)
      }
    }
  }, [])

  return (
    <div>
      <div
        className={clsx(
          'user-select-none fixed inset-0 z-40 flex flex-col items-center justify-center p-6',
          'pointer-events-none',
          countdown === null ? 'hidden' : 'visible',
        )}
      >
        <div
          ref={circleRef}
          id="countdown"
          className={clsx(
            'relative flex size-[256px] items-center justify-center overflow-hidden rounded-full md:size-[512px]',
            'font-barlow text-[128px] font-semibold text-white md:text-[256px]',
            'shadow-[0_0_128px_0_rgba(255,255,255,0.48)]',
          )}
        >
          {countdown !== null ? countdown.toFixed(0) : ''}
        </div>
        {gameMode === EGameMode.SINGLE_PLAYER && (
          <div className="mt-[48px] flex items-center justify-center">
            <div className="text-center text-[20px] uppercase leading-tight text-white">
              Gjør deg klar til å trykke
              <br />
              så fort du kan på sirkelen!
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
